import RAPIER from "@dimforge/rapier3d-compat";

export type RapierModule = typeof RAPIER;
export type RapierWorld = RAPIER.World;
export type RapierRigidBody = RAPIER.RigidBody;
export type RapierCollider = RAPIER.Collider;
export type RapierImpulseJoint = RAPIER.ImpulseJoint;

let initPromise: Promise<RapierModule> | null = null;

/** Load/init Rapier WASM once (compat build embeds wasm). */
export function initRapier(): Promise<RapierModule> {
  if (!initPromise) {
    initPromise = RAPIER.init().then(() => RAPIER);
  }
  return initPromise;
}

export interface PhysicsWorldOptions {
  gravity?: { x: number; y: number; z: number };
  /** Fixed simulation rate (Hz). */
  hz?: number;
}

/**
 * Rapier world wrapper: floor, fixed-timestep accumulator, quality Hz.
 */
export class PhysicsWorld {
  readonly R: RapierModule;
  world: RapierWorld;
  private floorBody: RapierRigidBody | null = null;
  private floorCollider: RapierCollider | null = null;
  private accumulator = 0;
  private hz: number;
  private maxSubsteps = 4;
  private disposed = false;

  constructor(R: RapierModule, options?: PhysicsWorldOptions) {
    this.R = R;
    const g = options?.gravity ?? { x: 0, y: -9.81, z: 0 };
    this.world = new R.World(g);
    this.hz = Math.max(15, options?.hz ?? 60);
    // Rapier integrates with world.timestep; keep it in lockstep with quality Hz.
    this.world.timestep = 1 / this.hz;
  }

  setHz(hz: number): void {
    this.hz = Math.max(15, hz);
    this.world.timestep = 1 / this.hz;
  }

  getHz(): number {
    return this.hz;
  }

  setGravity(x: number, y: number, z: number): void {
    this.world.gravity = { x, y, z };
  }

  /**
   * Static floor cuboid. Top surface at `y`; size is full width (XZ).
   * Collision groups (see boneColliders):
   * - Floor membership 0x0002, filter 0xFFFF (all humans).
   * - Human i membership bit (i+2), filter floor + other humans (no self).
   */
  setFloor(y: number, size: number): void {
    this.clearFloor();
    const R = this.R;
    const half = Math.max(0.5, size / 2);
    const thickness = 0.25;
    const bodyDesc = R.RigidBodyDesc.fixed().setTranslation(0, y - thickness / 2, 0);
    this.floorBody = this.world.createRigidBody(bodyDesc);
    // membership floor (0x0002), filter everything (humans + future props)
    const groups = (0x0002 << 16) | 0xffff;
    const collDesc = R.ColliderDesc.cuboid(half, thickness / 2, half)
      .setFriction(0.9)
      .setRestitution(0.02)
      .setCollisionGroups(groups)
      .setSolverGroups(groups);
    this.floorCollider = this.world.createCollider(collDesc, this.floorBody);
  }

  clearFloor(): void {
    if (this.floorCollider) {
      this.world.removeCollider(this.floorCollider, true);
      this.floorCollider = null;
    }
    if (this.floorBody) {
      this.world.removeRigidBody(this.floorBody);
      this.floorBody = null;
    }
  }

  /**
   * Fixed-step simulation. Returns number of substeps taken.
   * Independent of render dt; capped to avoid spiral-of-death.
   */
  step(dt: number): number {
    if (this.disposed || dt <= 0) return 0;
    const stepDt = 1 / this.hz;
    this.accumulator += Math.min(dt, stepDt * this.maxSubsteps);
    let steps = 0;
    while (this.accumulator >= stepDt && steps < this.maxSubsteps) {
      this.world.step();
      this.accumulator -= stepDt;
      steps++;
    }
    // Drop leftover if still behind after cap (prevents unbounded catch-up).
    if (this.accumulator > stepDt * 2) {
      this.accumulator = 0;
    }
    return steps;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearFloor();
    this.world.free();
  }
}
